import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSkipTask,
  familyKeys,
  newIdempotencyKey,
  resolveErrorMessage,
} from '@kidshome/api-contract';
import { Button } from '@kidshome/ui';
import { ActionSheet } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';

/**
 * `skip-task` — a gentle "skip for today" with kind, no-shame copy ("skipping won't break your
 * streak"). Confirms in a dialog, then optimistically removes nothing destructive — it simply asks
 * the server and invalidates the occurrence + the day so the new state reflects. No streak/loss copy.
 *
 * Carries an `Idempotency-Key` (the route is `@Idempotent`) so a skip attempted OFFLINE queues in
 * the PWA outbox and replays exactly once instead of being silently lost. A real failure keeps the
 * dialog open and surfaces a kind, translated message (`role=alert`) — never a silent no-op.
 */
export function SkipTask({
  occurrenceId,
  templateId,
  date,
  assigneeId,
  onSkipped,
  compact = false,
}: {
  /** The card's client-side id (`virtual:…` for an unlogged day) — used only for cache invalidation. */
  occurrenceId: string;
  /** The chore template; with `date` it forms the natural key the skip wires (ADR-0142). */
  templateId: string;
  date: string;
  assigneeId: string;
  onSkipped?: () => void;
  /** Render a small round icon trigger (for a compact list row) instead of a text button. */
  compact?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const mutation = useSkipTask({
    // Reconcile from the server on SETTLE (success OR error), mirroring the complete/redo features, so a
    // request that fails AFTER mutating server state still refreshes instead of leaving a stale cache.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: familyKeys.occurrence(occurrenceId) });
      void qc.invalidateQueries({ queryKey: familyKeys.day(assigneeId, date) });
      // Skipping changes the calendar's state pill for that day, so reconcile the week agenda too (its
      // own query isn't covered by the day key above) — otherwise /calendar stays stale until reload.
      void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeId) });
    },
    // Only CLOSE the sheet + notify on success — a real failure keeps the dialog open with its message.
    onSuccess: () => {
      setOpen(false);
      onSkipped?.();
    },
  });

  const errorMessage =
    mutation.error != null
      ? (() => {
          const m = resolveErrorMessage(mutation.error);
          return tDynamic(t, m.key, m.params);
        })()
      : null;

  const close = (): void => {
    mutation.reset();
    setOpen(false);
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('choreDetail.skip')}
          title={t('choreDetail.skip')}
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-text-muted outline-none transition-colors hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-ring"
        >
          {/* a "skip" glyph (skip-forward bars) */}
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
            <path
              d="M5 5l9 7-9 7V5zM19 5v14"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : (
        <Button variant="ghost" onClick={() => setOpen(true)}>
          {t('choreDetail.skip')}
        </Button>
      )}
      <ActionSheet
        open={open}
        onClose={close}
        title={t('choreDetail.skipTitle')}
        description={t('choreDetail.skipKind')}
        footer={
          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              onClick={() =>
                mutation.mutate({
                  optimisticId: occurrenceId,
                  templateId,
                  localDate: date,
                  assigneeId,
                  idempotencyKey: newIdempotencyKey(),
                })
              }
              disabled={mutation.isPending}
            >
              {t('choreDetail.skipConfirm')}
            </Button>
            <Button variant="ghost" onClick={close}>
              {t('choreDetail.skipCancel')}
            </Button>
          </div>
        }
      >
        {errorMessage ? (
          <p role="alert" className="text-center text-sm font-semibold text-danger">
            {errorMessage}
          </p>
        ) : (
          // The reassuring "won't break your streak" copy is the sheet's description; the body just
          // confirms the chore being skipped so the action is unambiguous before they tap.
          <p className="text-sm text-text-muted">{t('choreDetail.skipConfirmBody')}</p>
        )}
      </ActionSheet>
    </>
  );
}
